import type { ConfirmUploadMetadata } from '@omokage/shared';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, type PhotoListItem, type PhotoDetail } from '@/lib/api';
import { photoMetadataKeys } from './usePhotoMetadata';
import { generateThumbnail, compressImage, getImageDimensions } from '@/lib/imageUtils';

/**
 * Calculate SHA-256 hash of a file for duplicate detection
 */
async function calculateFileHash(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

// Query key factory for photos
export const photoKeys = {
  all: ['photos'] as const,
  lists: () => [...photoKeys.all, 'list'] as const,
  details: () => [...photoKeys.all, 'detail'] as const,
  detail: (id: string) => [...photoKeys.details(), id] as const,
};

// Upload photo input
export interface UploadPhotoInput {
  file: File;
  metadata?: ConfirmUploadMetadata;
  onProgress?: (progress: number) => void;
}

// Frontend page format for infinite query (kept for type compatibility)
export interface PhotoPage {
  items: PhotoListItem[];
  hasMore: boolean;
  cursor?: string;
}

/**
 * Hook for fetching a single photo by ID
 */
export function usePhoto(id: string | undefined) {
  return useQuery({
    queryKey: photoKeys.detail(id ?? ''),
    queryFn: async () => {
      if (!id) throw new Error('Photo ID is required');
      return apiClient.get<PhotoDetail>(`/photos/${id}`);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5分（写真詳細は変更頻度が低い）
  });
}

/**
 * Hook for uploading a photo using presigned URLs
 * Supports progress tracking and automatic cache invalidation
 *
 * New flow:
 * 1. Generate thumbnail and compressed versions locally
 * 2. Request presigned URLs from backend
 * 3. Upload all 3 files directly to R2
 * 4. Confirm upload with backend
 */
export function useUploadPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, metadata, onProgress }: UploadPhotoInput) => {
      // Step 1: Generate thumbnail, compressed versions, and content hash
      const [thumbnailBlob, compressedBlob, dimensions, contentHash] = await Promise.all([
        generateThumbnail(file, 200),
        compressImage(file, 0.8),
        getImageDimensions(file),
        calculateFileHash(file),
      ]);

      // Determine content type
      const contentType = file.type || 'image/jpeg';

      // Step 2: Request presigned URLs for upload
      const presignResponse = await apiClient.presignUpload([
        {
          filename: file.name,
          contentType,
          fileSize: file.size,
          contentHash, // Include hash for duplicate detection
        },
      ]);

      if (!presignResponse.uploads || presignResponse.uploads.length === 0) {
        throw new Error('Failed to get presigned upload URLs');
      }

      const uploadInfo = presignResponse.uploads[0];
      if (!uploadInfo) {
        throw new Error('No upload info returned');
      }

      const { uploadUrls, photoId } = uploadInfo;

      // Step 3: Upload all 3 files to R2 with progress tracking
      // Track progress for each file: original (60%), thumbnail (20%), compressed (20%)
      let originalProgress = 0;
      let thumbnailProgress = 0;
      let compressedProgress = 0;

      const calculateTotalProgress = () => {
        const total = originalProgress * 0.6 + thumbnailProgress * 0.2 + compressedProgress * 0.2;
        return Math.round(total);
      };

      const updateProgress = () => {
        if (onProgress) {
          onProgress(calculateTotalProgress());
        }
      };

      // Upload original, thumbnail, and compressed in parallel
      await Promise.all([
        apiClient.uploadToR2(uploadUrls.original, file, contentType, (progress) => {
          originalProgress = progress;
          updateProgress();
        }),
        apiClient.uploadToR2(uploadUrls.thumbnail, thumbnailBlob, 'image/jpeg', (progress) => {
          thumbnailProgress = progress;
          updateProgress();
        }),
        apiClient.uploadToR2(uploadUrls.compressed, compressedBlob, 'image/jpeg', (progress) => {
          compressedProgress = progress;
          updateProgress();
        }),
      ]);

      // Step 4: Confirm upload with backend
      const confirmResponse = await apiClient.confirmUpload({
        confirmations: [
          {
            photoId,
            metadata: {
              ...metadata,
              width: dimensions.width,
              height: dimensions.height,
            },
          },
        ],
      });

      if (!confirmResponse.results || confirmResponse.results.length === 0) {
        throw new Error('Failed to confirm upload');
      }

      const result = confirmResponse.results[0];
      if (!result) {
        throw new Error('No confirmation result returned');
      }

      if (!result.success) {
        throw new Error(result.error || 'Upload confirmation failed');
      }

      if (!result.photo) {
        throw new Error('No photo data returned from confirmation');
      }

      // If R2 upload or confirmation fails, the backend should handle cleanup
      return result.photo;
    },
    onSuccess: () => {
      // Invalidate photo metadata to refetch with new photo
      queryClient.invalidateQueries({
        queryKey: photoMetadataKeys.all,
      });
    },
  });
}

/**
 * Hook for deleting a photo
 * Invalidates photo metadata cache on success
 */
export function useDeletePhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (photoId: string) => {
      return apiClient.delete(`/photos/${photoId}`);
    },
    onSuccess: () => {
      // Invalidate photo metadata to refetch after deletion
      queryClient.invalidateQueries({
        queryKey: photoMetadataKeys.all,
      });
    },
  });
}
